import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lottie from "lottie-react";
import checkAnimationData from "../animations/checkAnimationData";
import Cookies from "js-cookie";
import API_ENDPOINTS from "../config/api";
import axios from "axios";
import { formatBuildingLabel } from "../utils/apartmentDisplay";

// 쿠키 설정 가져오기
const {
  KEYS: COOKIE_KEYS,
  EXPIRES: COOKIE_EXPIRES,
  OPTIONS: COOKIE_OPTIONS,
} = API_ENDPOINTS.COOKIE_CONFIG;

// 페이지 애니메이션 변형 설정
const pageVariants = {
  initial: {
    opacity: 0,
    y: 30,
  },
  animate: {
    opacity: 1,
    y: 0,
    transition: {
      type: "tween",
      ease: "easeOut",
      duration: 0.5,
    },
  },
  exit: {
    opacity: 0,
    y: -30,
    transition: {
      duration: 0.3,
    },
  },
};

function CompletionPage() {
  const navigate = useNavigate();
  const [claimData, setClaimData] = useState(null);
  const [coOwners, setCoOwners] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCoOwnerModal, setShowCoOwnerModal] = useState(false);

  useEffect(() => {
    fetchClaimData();
  }, []);

  // 접수내역 조회
  const fetchClaimData = async () => {
    try {
      const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);

      if (!accessToken) {
        setIsLoading(false);
        return;
      }

      const response = await axios.get(
        `${API_ENDPOINTS.BASE_URL}/mobile/claim`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": "application/json",
          },
        },
      );

      // 신분증이 실제로 제출되지 않았다면 완료 페이지를 보여주지 않고 제출 페이지로 돌려보냄
      if (!response.data || response.data.claimIdCard !== true) {
        navigate("/id-card-submit", { replace: true });
        return;
      }

      setClaimData(response.data);

      // 공동소유자 정보 가져오기
      const buildingUnitInfo = Cookies.get("buildingUnitInfo");
      if (buildingUnitInfo) {
        try {
          const parsedBuildingUnitInfo = JSON.parse(buildingUnitInfo);
          const coOwnersData = parsedBuildingUnitInfo.coOwners || [];
          setCoOwners(coOwnersData);

          // 공동소유자가 있으면 모달 표시
          if (coOwnersData.length > 0) {
            setShowCoOwnerModal(true);
            // 3초 후 자동으로 모달 닫기
            setTimeout(() => {
              setShowCoOwnerModal(false);
            }, 3000);
          }
        } catch (parseError) {
          console.error("공동소유자 정보 파싱 실패:", parseError);
        }
      }
    } catch (error) {
      console.error("접수내역 조회 실패:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 완료 버튼 클릭 핸들러
  const handleComplete = () => {
    // 토큰 가져오기
    const accessToken = Cookies.get(COOKIE_KEYS.ACCESS_TOKEN);
    const refreshToken = Cookies.get(COOKIE_KEYS.REFRESH_TOKEN);
    const apartmentCode = Cookies.get("apartmentCode");

    // 모든 상태 초기화
    localStorage.clear(); // 로컬 스토리지 초기화
    sessionStorage.clear(); // 세션 스토리지 초기화

    // GodeokUrbanBridge인 경우 특별 처리
    if (apartmentCode === "GodeokUrbanBridge") {
      // 히스토리 리셋하면서 루트로 이동
      window.location.replace("/");
      // 새로운 탭으로 URL 열기
      window.open("https://defect-litigation-yemige.vercel.app/v1", "_blank");
    } else {
      // 히스토리 스택을 리셋하면서 토큰과 함께 루트 경로로 이동
      window.location.replace(
        `/?accessToken=${accessToken}&refreshToken=${refreshToken}`,
      );
    }
  };

  // 사용자 정보 포맷
  const formatUserData = () => {
    if (!claimData) return {};

    return {
      dong: claimData.claimApartmentBlock || "000",
      ho: claimData.claimApartmentUnit || "0000",
      name: claimData.claimAssignmentName || "000",
      apartment:
        Cookies.get("apartmentCode") === "Richmond"
          ? "더리치먼드평창"
          : Cookies.get("apartmentCode") === "Raycounty"
            ? "레이카운티"
            : Cookies.get("apartmentCode") === "Harringtonplace"
              ? "해링턴플레이스GTX운정"
              : Cookies.get("apartmentCode") === "Singilpark"
                ? "신길파크자이"
                : Cookies.get("apartmentCode") === "DMCArtForest"
                  ? "DMC아트포레자이"
                  : Cookies.get("apartmentCode") === "GodeokUrbanBridge"
                    ? "e편한세상고덕어반브릿지"
                    : Cookies.get("apartmentCode") === "GeomdanUrbanCenter"
                      ? "e편한세상검단어반센트로"
                      : Cookies.get("apartmentCode") ===
                          "GeumgangPenteriumSeaglo"
                        ? "금강펜테리움더시글로"
                        : Cookies.get("apartmentCode") ===
                            "GeumgangPenteriumSeagloOffice"
                          ? "아라역금강펜테리움더시글로1차오피스텔"
                          : Cookies.get("apartmentCode") ===
                              "GangbyeonStarhills"
                            ? "덕소강변스타힐스"
                            : Cookies.get("apartmentCode") === "YangjuOkjeongLH"
                              ? "양주옥정LH엘리프"
                              : Cookies.get("apartmentCode") ===
                                  "UlsanGwellCityXiComplex1"
                                ? "울산지웰시티자이1단지"
                                : Cookies.get("apartmentCode") ===
                                    "YeongheungForestPrugioParkBien"
                                  ? "영흥숲푸르지오파크비엔"
                                  : Cookies.get("apartmentCode") ===
                                      "HillstateYonginGojinStationComplex1"
                                    ? "힐스테이트용인고진역1단지"
                                    : Cookies.get("apartmentCode") ===
                                        "HillstateYonginGojinStationComplex2"
                                      ? "힐스테이트용인고진역2단지"
                                      : Cookies.get("apartmentCode") ===
                                          "HillstateBongdamPrideCity"
                                        ? "힐스테이트봉담프라이드시티"
                                        : Cookies.get("apartmentCode") ===
                                            "PyeongchonIrvineFirstTheSharp"
                                          ? "평촌어바인퍼스트더샵"
                                          : Cookies.get("apartmentCode") ===
                                              "NaepoNewTownMoaMiraedMegacity2nd"
                                            ? "내포신도시모아미래도메가시티2차"
                                            : Cookies.get("apartmentCode") ===
                                                "BucheonIllumiState"
                                              ? "부천일루미스테이트"
                                              : Cookies.get("apartmentCode") ===
                                                  "SongdoStationSeohaeGrandbleThePark"
                                                ? "송도역서해그랑블더파크"
                                                : Cookies.get("apartmentCode") ===
                                                    "BupyeongHowstoryUrbanGreen"
                                                  ? "부평하우스토리어반그린"
                                                  : Cookies.get("apartmentCode") ===
                                                      "ForeonStation5"
                                                    ? "포레온스테이션5"
                                                    : Cookies.get("apartmentCode") ===
                                                        "HyundaiTerraTowerGuriGalmae"
                                                      ? "현대테라타워구리갈매"
                                                      : Cookies.get("apartmentCode") === "테스트"
                                                        ? "테스트아파트"
                                                        : "",
    };
  };

  const userData = formatUserData();

  if (isLoading) {
    return (
      <motion.div
        className="page-container"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        style={{
          height: "100vh",
          background: "#FFFFFF",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div>접수내역을 불러오는 중...</div>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="page-container"
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      <div className="completion-content">
        <div className="animation-container">
          <Lottie
            animationData={checkAnimationData}
            loop={false}
            autoplay={true}
            style={{ width: 160, height: 160 }}
          />
        </div>
        {/* <img
          src="/completion.png"
          alt="completion"
          style={{ width: 73, height: 30, marginTop: -20, marginBottom: 20 }}
        /> */}

        <motion.div
          className="completion-message"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.5 }}
          style={{
            textAlign: "center",
            alignSelf: "center",
            width: "100%",
          }}
        >
          <h2>채권양도·양수 동의가</h2>
          <h3>모두 완료되었습니다.</h3>
          {/* <h4
            style={{
              color: "#4B5563",
            }}
          >
            해당 내용을 확인해주세요
          </h4> */}
        </motion.div>

        <div className="contract-summary">
          <h3>주요 내용 요약</h3>
          <div className="summary-row">
            <div className="summary-label">동·호수</div>
            <div className="summary-value">
              {formatBuildingLabel(
                userData.dong,
                Cookies.get("apartmentCode"),
              )}{" "}
              {userData.ho}호
            </div>
          </div>
          <div className="summary-row">
            <div className="summary-label">소유자 성명</div>
            <div className="summary-value">{userData.name}</div>
          </div>
          {coOwners.length > 0 && (
            <div className="summary-row">
              <div className="summary-label">공동소유자</div>
              <div className="summary-value">
                {coOwners.map((coOwner, index) => (
                  <span key={index}>
                    {coOwner.name}
                    {index < coOwners.length - 1 ? ", " : ""}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="summary-row">
            <div className="summary-label">양수인</div>
            {(() => {
              const apartmentText = userData.apartment;
              const isLongText = apartmentText.length > 10;
              const orgLabel =
                Cookies.get("apartmentCode") === "ForeonStation5"
                  ? "관리단"
                  : "입주자대표회의";

              return isLongText ? (
                <div
                  className="summary-value"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: "4px",
                  }}
                >
                  <span>{apartmentText}</span>
                  <span>{orgLabel}</span>
                </div>
              ) : (
                <div className="summary-value">
                  {apartmentText} {orgLabel}
                </div>
              );
            })()}
          </div>
          {/* <div className="summary-row">
            <div className="summary-label">철회 여부</div>
            <div className="summary-value">불가</div>
          </div> */}
        </div>
      </div>

      <motion.button
        className="payment-button"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.5 }}
        onClick={handleComplete}
      >
        완료
      </motion.button>

      {/* 공동소유자 알림 모달 */}
      {showCoOwnerModal && (
        <motion.div
          className="co-owner-modal"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          style={{
            position: "fixed",
            top: "10px",
            left: 0,
            right: 0,
            margin: "0 auto",
            backgroundColor: "#7A4FFF",
            color: "white",
            padding: "12px 24px",
            borderRadius: "8px",
            zIndex: 1000,
            width: "90%",
            maxWidth: "400px",
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
          }}
        >
          <p style={{ margin: 0, fontSize: "14px", lineHeight: "1.4" }}>
            {coOwners.map((coOwner, index) => (
              <span key={index}>
                {coOwner.name}
                {index < coOwners.length - 1 ? ", " : ""}
              </span>
            ))}
            님에게 알림톡을 전송했습니다.
          </p>
        </motion.div>
      )}
    </motion.div>
  );
}

export default CompletionPage;
